import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Card } from '../ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../ui/alert'
import { supabase } from '../../lib/supabase'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../lib/auth.context'

interface TOTPEnrollmentProps {
  onComplete?: () => void
  redirectTo?: string
}

export function TOTPEnrollment({ onComplete, redirectTo }: TOTPEnrollmentProps) {
  const [qrCode, setQrCode] = useState<string>('')
  const [secret, setSecret] = useState<string>('')
  const [verifyCode, setVerifyCode] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [factorId, setFactorId] = useState<string>('')
  const [setupError, setSetupError] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t } = useTranslation()
  const { setIsTOTPVerified } = useAuth()

  useEffect(() => {
    const setupTOTP = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user?.email) throw new Error('User email not found')

        // First, check for existing factors and clean them up
        const { data: existingFactors } = await supabase.auth.mfa.listFactors()
        console.log('[TOTPEnrollment] Existing factors:', existingFactors)
        
        // Remove any existing TOTP factors (both verified and unverified) to avoid conflicts
        const allTotpFactors = existingFactors?.totp || []
        const allFactors = existingFactors?.all || []
        
        // Check all factors, not just TOTP array, as factors might be in different states
        const totpFactorsToRemove = allFactors.filter(factor => factor.factor_type === 'totp')
        
        for (const factor of totpFactorsToRemove) {
          console.log('[TOTPEnrollment] Removing existing TOTP factor:', factor.id, 'status:', factor.status)
          try {
            await supabase.auth.mfa.unenroll({ factorId: factor.id })
            console.log('[TOTPEnrollment] Successfully removed factor:', factor.id)
          } catch (unenrollError) {
            console.warn('[TOTPEnrollment] Failed to remove factor:', factor.id, unenrollError)
            // Continue anyway, the enrollment might still work
          }
        }

        console.log('[TOTPEnrollment] Starting enrollment process')
        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: `${t('title')} - ${user.email}`
        })

        if (error) {
          console.error('[TOTPEnrollment] Enrollment failed:', error)
          throw error
        }

        console.log('[TOTPEnrollment] Enrollment successful:', data)
        if (data.totp) {
          setQrCode(data.totp.qr_code)
          setSecret(data.totp.secret)
          setFactorId(data.id)
          console.log('[TOTPEnrollment] QR code and secret set successfully')
        } else {
          console.error('[TOTPEnrollment] No TOTP data in enrollment response')
          throw new Error('No TOTP data received from enrollment')
        }
      } catch (error: any) {
        console.error('[TOTPEnrollment] Setup failed:', error)
        let errorMessage = error.message || 'Failed to setup TOTP'
        
        // Handle specific error cases
        if (error.message?.includes('already exists') || error.status === 422) {
          errorMessage = 'A TOTP device is already being set up. Please try the "Reset and Retry" button below.'
          setSetupError(true)
        }
        
        setFormError(errorMessage)
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        })
      }
    }

    setupTOTP()
  }, [toast, t])

  const handleReset = async () => {
    console.log('[TOTPEnrollment] Manual reset triggered')
    setFormError('')
    setSetupError(false)
    setQrCode('')
    setSecret('')
    setFactorId('')
    
    // Force a fresh setup
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.email) throw new Error('User email not found')

      // More aggressive cleanup - get all factors and remove TOTP ones
      const { data: existingFactors } = await supabase.auth.mfa.listFactors()
      console.log('[TOTPEnrollment] Reset - existing factors:', existingFactors)
      
      const allFactors = existingFactors?.all || []
      const totpFactorsToRemove = allFactors.filter(factor => factor.factor_type === 'totp')
      
      for (const factor of totpFactorsToRemove) {
        console.log('[TOTPEnrollment] Reset - removing factor:', factor.id)
        try {
          await supabase.auth.mfa.unenroll({ factorId: factor.id })
        } catch (err) {
          console.warn('[TOTPEnrollment] Reset - failed to remove factor:', err)
        }
      }

      // Wait a moment then try enrollment again
      setTimeout(async () => {
        try {
          const { data, error } = await supabase.auth.mfa.enroll({
            factorType: 'totp',
            friendlyName: `${t('title')} - ${user.email}`
          })

          if (error) throw error

          if (data.totp) {
            setQrCode(data.totp.qr_code)
            setSecret(data.totp.secret)
            setFactorId(data.id)
            console.log('[TOTPEnrollment] Reset successful')
          }
        } catch (resetError: any) {
          console.error('[TOTPEnrollment] Reset failed:', resetError)
          setFormError(resetError.message || 'Reset failed')
        }
      }, 1000)
    } catch (error: any) {
      console.error('[TOTPEnrollment] Reset error:', error)
      setFormError(error.message || 'Reset failed')
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setIsLoading(true)

    try {
      const { data: challenge, error } = await supabase.auth.mfa.challenge({
        factorId: factorId
      })

      if (error) throw error

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        challengeId: challenge.id,
        code: verifyCode
      })

      if (verifyError) throw verifyError

      toast({
        title: 'Success',
        description: 'TOTP device enrolled successfully',
      })

      // Refresh the session to ensure we have the latest token
      const { data: { session: newSession }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) throw sessionError

      // Ensure we have a valid session before redirecting
      if (!newSession?.access_token) {
        throw new Error('Failed to initialize session after TOTP enrollment')
      }

      // Set TOTP verification status after successful enrollment
      console.log('[TOTPEnrollment] Setting TOTP verification to true after successful enrollment')
      setIsTOTPVerified(true)

      if (onComplete) {
        onComplete()
      }

      // Show success message and redirect to admin dashboard
      toast({
        title: 'Setup Complete',
        description: 'Your account has been set up successfully. Redirecting to dashboard...',
      })

      // Short delay to show the success message
      setTimeout(() => {
        if (redirectTo) {
          // Check if it's an external URL (starts with http:// or https://)
          if (redirectTo.startsWith('http://') || redirectTo.startsWith('https://')) {
            window.location.href = redirectTo
          } else {
            navigate(redirectTo)
          }
        } else {
          navigate('/admin/dashboard')
        }
      }, 1500)
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to verify TOTP'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
      <h1 className={`mb-6 text-2xl font-bold ${getThemeClass('components.text.heading')}`}>
        Set Up Two-Factor Authentication
      </h1>
      <div className="space-y-4">
        <div className="text-center">
          {qrCode ? (
            <div className="mb-4">
              <img src={qrCode} alt="QR Code" className="mx-auto" />
            </div>
          ) : (
            <div className="mb-4 p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Generating QR code...</p>
            </div>
          )}
          {secret && (
            <div className="mb-4">
              <p className="text-sm text-gray-600">Manual entry code:</p>
              <code className="block p-2 bg-gray-100 rounded">{secret}</code>
            </div>
          )}
          {qrCode && (
            <p className={`mb-4 ${getThemeClass('components.text.body')}`}>
              Scan the QR code or enter the secret key manually in your authenticator app
            </p>
          )}
        </div>
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <Input
              type="text"
              placeholder="Enter verification code"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value)}
              required
              disabled={isLoading}
              className={getThemeClass('components.input.base')}
            />
          </div>
          {formError && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <Button
            type="submit"
            className={`w-full ${getThemeClass('components.button.primary')}`}
            disabled={isLoading || !factorId}
          >
            {isLoading ? 'Verifying...' : 'Verify and Complete Setup'}
          </Button>
          {setupError && (
            <Button
              type="button"
              variant="outline"
              className="w-full mt-2"
              onClick={handleReset}
              disabled={isLoading}
            >
              Reset and Retry
            </Button>
          )}
        </form>
      </div>
    </Card>
  )
}
